// v2/components/admin/AnnouncementModal.js — gorsel duyuru ekleme / duzenleme
// Kabuk stilleri UserFormModal ile ortak (s), duyuruya ozel alanlar (a).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Save, Loader2, AlertTriangle, UploadCloud, RefreshCw } from 'lucide-react';
import { adminAnnouncementApi, itemdenAlanlar } from '../../hooks/useAdminAnnouncements';
import s from './UserFormModal.module.css';
import a from './AnnouncementModal.module.css';

const TURLER = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BOYUT = 3 * 1024 * 1024;
const BOS = { baslik: '', link: '', baslangic: '', bitis: '', aktif: true, sira: 0 };

function mb(n) {
  return `${(n / 1024 / 1024).toFixed(2).replace('.', ',')} MB`;
}

/** Onizleme icin olusturulan gecici URL'yi serbest birakir */
function birak(ref) {
  if (ref.current) {
    URL.revokeObjectURL(ref.current);
    ref.current = '';
  }
}

export default function AnnouncementModal({ acik, duyuru, onKapat, onKaydedildi, yayinBos = 5 }) {
  const duzenleme = Boolean(duyuru);
  const [form, setForm] = useState(BOS);
  const [dosya, setDosya] = useState(null);
  const [onizleme, setOnizleme] = useState('');
  const [olcu, setOlcu] = useState(null);
  const [surukle, setSurukle] = useState(false);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState(null);
  const inputRef = useRef(null);
  const urlRef = useRef('');

  useEffect(() => {
    if (!acik) return;
    setHata(null);
    setDosya(null);
    setOlcu(null);
    setSurukle(false);
    birak(urlRef);
    setForm(duzenleme ? itemdenAlanlar(duyuru) : { ...BOS, aktif: yayinBos > 0 });
    setOnizleme(duzenleme ? duyuru.image : '');
  }, [acik, duzenleme, duyuru, yayinBos]);

  useEffect(() => () => birak(urlRef), []);

  useEffect(() => {
    if (!acik) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !kaydediyor) onKapat(); };
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [acik, kaydediyor, onKapat]);

  const degis = useCallback((alan) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [alan]: v }));
  }, []);

  const dosyaSec = useCallback((f) => {
    if (!f) return;
    if (!TURLER.includes(f.type)) {
      setHata('Sadece JPG, PNG veya WEBP görsel yüklenebilir');
      return;
    }
    if (f.size > MAX_BOYUT) {
      setHata(`Görsel en fazla 3 MB olabilir (seçilen: ${mb(f.size)})`);
      return;
    }
    setHata(null);
    birak(urlRef);
    const url = URL.createObjectURL(f);
    urlRef.current = url;
    setDosya(f);
    setOnizleme(url);
    setOlcu(null);
  }, []);

  const secimAc = () => { if (inputRef.current && !kaydediyor) inputRef.current.click(); };

  const kaydet = useCallback(async () => {
    if (kaydediyor) return;
    setHata(null);
    if (!duzenleme && !dosya) { setHata('Lütfen bir görsel seçin'); return; }
    const link = form.link.trim();
    if (link && !/^(https?:\/\/|\/(?!\/))/.test(link)) {
      setHata('Link http://, https:// ya da / ile başlamalı'); return;
    }
    if (form.baslangic && form.bitis && form.bitis < form.baslangic) {
      setHata('Bitiş tarihi başlangıçtan önce olamaz'); return;
    }
    const alanlar = { ...form, baslik: form.baslik.trim(), link };
    setKaydediyor(true);
    try {
      const v = duzenleme
        ? await adminAnnouncementApi.guncelle(duyuru.id, alanlar, dosya)
        : await adminAnnouncementApi.ekle(alanlar, dosya);
      if (!v || !v.success) throw new Error((v && v.message) || 'Kaydedilemedi');
      onKaydedildi(duzenleme ? 'Duyuru güncellendi' : 'Duyuru eklendi');
    } catch (e) {
      setHata((e && e.message) || 'Kaydedilemedi');
    } finally {
      setKaydediyor(false);
    }
  }, [form, dosya, duzenleme, duyuru, kaydediyor, onKaydedildi]);

  if (!acik) return null;

  let uyari = null;
  if (olcu) {
    const oran = olcu.w / olcu.h;
    if (olcu.w < 800) {
      uyari = 'Görsel küçük; kartta bulanık görünebilir (en az 800 px genişlik önerilir).';
    } else if (oran < 1.3 || oran > 2.1) {
      uyari = 'Görsel oranı önerilenden (8:5) çok farklı; kartta kenarları kırpılabilir.';
    }
  }

  // duzenlenen kayit zaten bir yayin hakki kullaniyorsa sinira takilmaz
  const sayiliyor = duzenleme && duyuru.active && duyuru.status !== 'bitti';
  const yayinDolu = yayinBos <= 0 && !sayiliyor;

  return (
    <div className={s.overlay} role="dialog" aria-modal="true"
         aria-label={duzenleme ? 'Duyuru düzenle' : 'Yeni duyuru'}>
      <div className={s.backdrop} onClick={() => !kaydediyor && onKapat()} aria-hidden="true" />
      <div className={s.modal}>
        <div className={s.head}>
          <div className={s.headText}>
            <h3 className={s.title}>{duzenleme ? 'Duyuruyu Düzenle' : 'Yeni Duyuru'}</h3>
            <p className={s.sub}>Ana sayfadaki duyuru kartında gösterilecek görsel</p>
          </div>
          <button type="button" className={s.closeBtn} onClick={onKapat}
                  disabled={kaydediyor} aria-label="Kapat">
            <X size={18} />
          </button>
        </div>

        <form className={s.body} onSubmit={(e) => { e.preventDefault(); kaydet(); }}>
          {hata && (
            <div className={s.error}><AlertTriangle size={16} /> <span>{hata}</span></div>
          )}

          <div
            className={`${a.drop} ${surukle ? a.dropOn : ''} ${onizleme ? a.dropHas : ''}`}
            onDragOver={(e) => { e.preventDefault(); if (!surukle) setSurukle(true); }}
            onDragLeave={() => setSurukle(false)}
            onDrop={(e) => {
              e.preventDefault();
              setSurukle(false);
              dosyaSec(e.dataTransfer.files && e.dataTransfer.files[0]);
            }}
            onClick={secimAc}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); secimAc(); }
            }}
            role="button" tabIndex={0} aria-label="Görsel seç"
          >
            {onizleme ? (
              <>
                <img
                  className={a.preview} src={onizleme} alt=""
                  onLoad={(e) => setOlcu({
                    w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight,
                  })}
                />
                <span className={a.change}><RefreshCw size={14} /> Görseli değiştir</span>
              </>
            ) : (
              <span className={a.dropEmpty}>
                <UploadCloud size={30} strokeWidth={1.5} />
                <b>Görseli sürükleyip bırakın</b>
                <span>ya da tıklayıp seçin</span>
                <small>JPG, PNG, WEBP · en fazla 3 MB · önerilen 1200 × 750 px</small>
              </span>
            )}
          </div>
          <input
            ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
            onChange={(e) => { dosyaSec(e.target.files && e.target.files[0]); e.target.value = ''; }}
          />

          {(dosya || olcu) && (
            <div className={a.fileInfo}>
              {dosya && <span>{dosya.name} · {mb(dosya.size)}</span>}
              {olcu && <span>{olcu.w} × {olcu.h} px</span>}
            </div>
          )}
          {uyari && <div className={a.warn}><AlertTriangle size={14} /> <span>{uyari}</span></div>}

          <div className={s.grid}>
            <label className={`${s.field} ${s.wide}`}>
              <span className={s.label}>
                Başlık <i className={s.hint}>müşteriye görünmez, görselin alt metni olur</i>
              </span>
              <input className={s.input} value={form.baslik} onChange={degis('baslik')}
                     placeholder="Örn. Ekim silecek kampanyası" maxLength={150} />
            </label>

            <label className={`${s.field} ${s.wide}`}>
              <span className={s.label}>
                Link <i className={s.hint}>boş bırakılırsa görsel tıklanamaz</i>
              </span>
              <input className={s.input} value={form.link} onChange={degis('link')}
                     placeholder="/v2/products?campaign=general ya da https://…" maxLength={500} />
            </label>

            <label className={s.field}>
              <span className={s.label}>Başlangıç <i className={s.hint}>boşsa hemen</i></span>
              <input className={s.input} type="date" value={form.baslangic}
                     onChange={degis('baslangic')} />
            </label>
            <label className={s.field}>
              <span className={s.label}>Bitiş <i className={s.hint}>boşsa süresiz</i></span>
              <input className={s.input} type="date" value={form.bitis}
                     min={form.baslangic || undefined} onChange={degis('bitis')} />
            </label>

            <label className={`${a.check} ${s.wide}`}>
              <input type="checkbox" checked={form.aktif} onChange={degis('aktif')} />
              Aktif (tarih aralığındaysa ana sayfada gösterilir)
            </label>
            {yayinDolu && form.aktif && (
              <span className={`${a.warn} ${s.wide}`}>
                <AlertTriangle size={14} />
                <span>Yayın sınırı dolu. Aktif kaydetmek için önce başka bir duyuruyu
                  pasife alın ya da bunu pasif kaydedin.</span>
              </span>
            )}
          </div>
        </form>

        <div className={s.foot}>
          <button type="button" className={s.cancelBtn} onClick={onKapat}
                  disabled={kaydediyor}>İptal</button>
          <button type="button" className={s.saveBtn} onClick={kaydet} disabled={kaydediyor}>
            {kaydediyor ? <Loader2 size={16} className={s.spin} /> : <Save size={16} />}
            {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
